import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { getIDFromUrl } from '@rossum/api-client';
import { DeleteConfirmationDialog } from '@rossum/components/delete-confirmation-dialog/DeleteConfirmationDialog';
import { DetailDrawer } from '@rossum/ui';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grow,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';
import { absoluteApiUrl } from '../../../constants/config';
import {
  canManageQueueSelector,
  isEmpoweredManager,
  isUserAdmin,
  userSelector,
} from '../../../redux/modules/user/selectors';
import { State } from '../../../types/state';
import { FilterPanel } from '../../document-list-filtering/FilterPanel';
import {
  FilterColDef,
  ValidatedGridFilterItem,
} from '../../document-list-filtering/types';
import { CodecFieldTypes } from '../../mql/constants';
import { detailsEncoder } from '../../mql/detailsCodec';
import { encodeMqlQuery } from '../../mql/encodeMqlQuery';
import { MultipleQueueDialog } from '../../queues/select-queue/MultipleQueueDialog';
import { NormalizedSavedView } from '../core';
import {
  useCreateSavedView,
  useDeleteSavedView,
  useUpdateSavedView,
} from '../hooks';
import { AssignedQueuesList } from './AssignedQueuesList';

export type SavedViewDrawerState =
  | { type: 'create'; filtering: GridFilterItem[] }
  | { type: 'edit'; savedView: NormalizedSavedView };

// While the drawer stays in an `edit` state, the user can switch it into a
// transient `duplicate` mode. This creates a brand new view pre-filled with the
// saved view's values, so it behaves like `create` on submit but derives its
// defaults from an existing view.
type EffectiveDrawerState =
  | SavedViewDrawerState
  | { type: 'duplicate'; savedView: NormalizedSavedView };

const getDuplicateName = (intl: IntlShape, viewName: string) =>
  intl.formatMessage(
    { id: 'containers.savedViews.drawer.duplicateNameSuffix' },
    { viewName }
  );

const getDrawerTitle = (intl: IntlShape, state: EffectiveDrawerState) =>
  state.type === 'edit'
    ? intl.formatMessage(
        { id: 'containers.savedViews.drawer.editTitle' },
        { viewName: state.savedView.name }
      )
    : state.type === 'duplicate'
      ? intl.formatMessage(
          { id: 'containers.savedViews.drawer.duplicateTitle' },
          { viewName: getDuplicateName(intl, state.savedView.name) }
        )
      : intl.formatMessage({
          id: 'containers.savedViews.drawer.createTitle',
        });

type SavedViewsConfigDrawerProps = {
  state: SavedViewDrawerState | null;
  columns: FilterColDef[];
  activeQueueId: number;
  codecFieldTypes: CodecFieldTypes;
  onClose: () => void;
  onDelete: (viewId: string) => void;
};

export const SavedViewsConfigDrawer = ({
  state,
  columns,
  onClose,
  activeQueueId,
  codecFieldTypes,
  onDelete,
}: SavedViewsConfigDrawerProps) => {
  const intl = useIntl();
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Reset the transient duplicate mode whenever the drawer targets a different
  // view (or closes), so reopening always starts from a clean edit/create form.
  const stateKey =
    state == null
      ? null
      : state.type === 'edit'
        ? state.savedView.id
        : 'create';
  useEffect(() => {
    setIsDuplicating(false);
  }, [stateKey]);

  const effectiveState: EffectiveDrawerState | null =
    state == null
      ? null
      : state.type === 'edit' && isDuplicating
        ? { type: 'duplicate', savedView: state.savedView }
        : state;

  const handleClose = () => {
    setIsDuplicating(false);
    onClose();
  };

  return (
    <DetailDrawer
      open={!!state}
      onClose={handleClose}
      title={effectiveState ? getDrawerTitle(intl, effectiveState) : undefined}
      size="small"
    >
      {effectiveState ? (
        // Remount the form when switching between edit and duplicate so it
        // fades back in with the freshly derived default values.
        <Grow key={effectiveState.type} in appear>
          <Stack sx={{ height: 1 }}>
            <SavedViewForm
              state={effectiveState}
              columns={columns}
              activeQueueId={activeQueueId}
              codecFieldTypes={codecFieldTypes}
              onClose={handleClose}
              onDelete={onDelete}
              onDuplicate={() => setIsDuplicating(true)}
            />
          </Stack>
        </Grow>
      ) : null}
    </DetailDrawer>
  );
};

type SavedViewFormValues = {
  name: string;
  filtering: GridFilterItem[];
  shared: boolean;
  queuesFilter: string[];
};

const createSavedViewSchema = (messages: {
  nameRequired: string;
  filtersRequired: string;
}): z.ZodType<SavedViewFormValues> =>
  z.object({
    name: z.string().min(1, messages.nameRequired),
    filtering: z
      .array(z.custom<GridFilterItem>())
      .min(1, messages.filtersRequired),
    shared: z.boolean(),
    queuesFilter: z.array(z.string()),
  });

const getDefaultValues = ({
  intl,
  state,
  activeQueueId,
  canEditSharing,
}: {
  intl: IntlShape;
  state: EffectiveDrawerState;
  activeQueueId: number;
  canEditSharing: boolean;
}): SavedViewFormValues =>
  state.type === 'edit'
    ? {
        name: state.savedView.name,
        filtering: state.savedView.parsedQuery,
        shared: state.savedView.shared,
        queuesFilter: state.savedView.queuesFilter,
      }
    : state.type === 'duplicate'
      ? {
          name: getDuplicateName(intl, state.savedView.name),
          filtering: state.savedView.parsedQuery,
          // A user without sharing rights cannot submit a shared view, so
          // default the duplicate to private rather than locking them into an
          // "Everyone" value the API would reject.
          shared: state.savedView.shared && canEditSharing,
          queuesFilter: state.savedView.queuesFilter,
        }
      : {
          name: '',
          filtering: state.filtering,
          shared: false,
          queuesFilter: [getQueueUrlFormId(activeQueueId)],
        };

const getQueueUrlFormId = (id: number) => `${absoluteApiUrl}/queues/${id}`;

type SavedViewFormProps = {
  state: EffectiveDrawerState;
  columns: FilterColDef[];
  activeQueueId: number;
  codecFieldTypes: CodecFieldTypes;
  onClose: () => void;
  onDelete: (viewId: string) => void;
  onDuplicate: () => void;
};

const SavedViewForm = ({
  state,
  columns,
  activeQueueId,
  codecFieldTypes,
  onClose,
  onDelete,
  onDuplicate,
}: SavedViewFormProps) => {
  const intl = useIntl();
  const [openQueueDialog, setOpenQueueDialog] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { mutate: createSavedView, isLoading: isCreating } =
    useCreateSavedView();
  const { mutate: updateSavedView, isLoading: isUpdating } =
    useUpdateSavedView();
  const { mutate: deleteSavedView, isLoading: isDeleting } =
    useDeleteSavedView();

  const isSaving = isCreating || isUpdating;

  const isAdmin = useSelector(isUserAdmin);
  const isEmpManager = useSelector(isEmpoweredManager);
  const currentUserUrl = useSelector(userSelector).url;
  // A view being created or duplicated is owned by the current user; an
  // existing view is owned by whoever created it.
  const isCreator =
    state.type !== 'edit' || state.savedView.createdBy === currentUserUrl;

  const savedViewQueueUrls =
    state.type === 'create'
      ? [getQueueUrlFormId(activeQueueId)]
      : state.savedView.queuesFilter;

  // Editability itself comes from the API (`isEditable`), but sharing has no
  // flag of its own yet, so it stays client-side: an empowered manager may only
  // change the visibility of a view whose every queue they manage — a view
  // spanning a queue they don't own would otherwise let them expose queues
  // outside their scope. An empty `queuesFilter` means the view is available
  // from every queue the user is assigned to, and `GET /queues` is
  // caller-scoped which means it will only return queues that user is assigned
  // to — so there the role check alone is enough (`[].every` is vacuously
  // true).
  const isEmpManagerForAllSavedViewQueues = useSelector(
    (reduxState: State) =>
      isEmpManager &&
      savedViewQueueUrls.every(queueUrl =>
        canManageQueueSelector(reduxState, queueUrl)
      )
  );
  const canEditView = state.type === 'edit' ? state.savedView.isEditable : true;
  const canEditSharing =
    canEditView && isCreator && (isAdmin || isEmpManagerForAllSavedViewQueues);

  const schema = useMemo(
    () =>
      createSavedViewSchema({
        nameRequired: intl.formatMessage({
          id: 'containers.savedViews.drawer.nameRequired',
        }),
        filtersRequired: intl.formatMessage({
          id: 'containers.savedViews.drawer.filtersRequired',
        }),
      }),
    [intl]
  );

  const { control, handleSubmit, watch } = useForm<SavedViewFormValues>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues({
      intl,
      state,
      activeQueueId,
      canEditSharing,
    }),
  });

  const handleFormSubmit = handleSubmit(values => {
    const payload = {
      name: values.name,
      query: encodeMqlQuery(values.filtering, {
        codecFieldTypes,
        columnEncoders: { details: detailsEncoder },
      }),
      shared: values.shared,
      queuesFilter: values.queuesFilter,
    };

    if (state.type === 'edit') {
      updateSavedView(
        { savedViewId: state.savedView.id, payload },
        { onSuccess: onClose }
      );
    } else {
      createSavedView(payload, { onSuccess: onClose });
    }
  });

  const handleConfirmDelete = () => {
    if (state.type !== 'edit') return;

    deleteSavedView(
      { savedViewId: state.savedView.id, viewName: state.savedView.name },
      {
        onSuccess: () => {
          setIsDeleteDialogOpen(false);
          onClose();
          onDelete(`${state.savedView.id}`);
        },
      }
    );
  };

  const queueCount = watch('queuesFilter').length;

  return (
    <Stack sx={{ height: 1 }} component="form" onSubmit={handleFormSubmit}>
      <Stack
        sx={{ py: 3, px: 2, flex: 1, overflowY: 'auto' }}
        spacing={3}
        useFlexGap
      >
        {!canEditView ? (
          <Alert severity="warning">
            {intl.formatMessage({ id: 'containers.savedViews.readOnlyAlert' })}
          </Alert>
        ) : null}
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={intl.formatMessage({
                id: 'containers.savedViews.drawer.viewNameLabel',
              })}
              size="small"
              disabled={!canEditView}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              autoFocus
            />
          )}
        />
        <Controller
          control={control}
          name="shared"
          render={({ field }) => (
            <FormControl disabled={!canEditSharing}>
              <Stack spacing={0.5} useFlexGap>
                <Typography variant="h6">
                  {intl.formatMessage({
                    id: 'containers.savedViews.drawer.visibilityLabel',
                  })}
                </Typography>
                <RadioGroup
                  value={field.value ? 'shared' : 'onlyMe'}
                  onChange={(_, value) => field.onChange(value === 'shared')}
                >
                  <FormControlLabel
                    value="onlyMe"
                    control={<Radio size="small" sx={{ px: 1, py: 0.5 }} />}
                    label={intl.formatMessage({
                      id: 'containers.savedViews.drawer.visibilityOnlyMe',
                    })}
                    slotProps={{
                      typography: {
                        fontSize: t => t.typography.body2.fontSize,
                      },
                    }}
                  />
                  <FormControlLabel
                    value="shared"
                    control={<Radio size="small" sx={{ px: 1, py: 0.5 }} />}
                    label={intl.formatMessage({
                      id: 'containers.savedViews.drawer.visibilityEveryone',
                    })}
                    slotProps={{
                      typography: {
                        fontSize: t => t.typography.body2.fontSize,
                      },
                    }}
                  />
                </RadioGroup>
              </Stack>
            </FormControl>
          )}
        />
        <Stack spacing={0.5} useFlexGap>
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {intl.formatMessage({
                id: 'containers.savedViews.drawer.queuesTitle',
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {queueCount !== 0
                ? queueCount
                : intl.formatMessage({
                    id: 'containers.savedViews.allQueuesCount',
                  })}
            </Typography>
          </Stack>
          <Controller
            control={control}
            name="queuesFilter"
            render={({ field, fieldState }) => {
              return (
                <Stack spacing={1} useFlexGap>
                  <Stack>
                    <MultipleQueueDialog
                      open={openQueueDialog}
                      title={intl.formatMessage({
                        id: 'containers.savedViews.drawer.selectQueueDialog.title',
                      })}
                      onClose={() => setOpenQueueDialog(false)}
                      onConfirm={queues => {
                        field.onChange(queues.map(q => q.url));
                        setOpenQueueDialog(false);
                      }}
                      preSelectedQueueIds={field.value.map(url =>
                        getIDFromUrl(url)
                      )}
                    />
                    <AssignedQueuesList
                      queueUrls={field.value}
                      onRemove={queueUrl =>
                        field.onChange(
                          field.value.filter(url => url !== queueUrl)
                        )
                      }
                      isReadOnly={!canEditView}
                    />
                    {fieldState.error ? (
                      <FormHelperText error>
                        {fieldState.error.message}
                      </FormHelperText>
                    ) : null}
                  </Stack>
                  <Button
                    color="secondary"
                    variant="outlined"
                    disabled={!canEditView}
                    onClick={() => setOpenQueueDialog(true)}
                    sx={{ alignSelf: 'flex-end' }}
                  >
                    {intl.formatMessage({
                      id: 'containers.savedViews.drawer.addQueuesButton',
                    })}
                  </Button>
                </Stack>
              );
            }}
          />
        </Stack>
        <Stack spacing={0.5} useFlexGap>
          <Typography variant="h6">
            {intl.formatMessage({
              id: 'containers.savedViews.drawer.filtersTitle',
            })}
          </Typography>
          {/* FilterPanel has no disabled state, so make the whole region
              non-interactive when the user cannot edit the view. */}
          <Stack
            aria-disabled={!canEditView}
            data-cy="saved-view-filters-region"
            sx={
              canEditView ? undefined : { pointerEvents: 'none', opacity: 0.5 }
            }
          >
            <Controller
              control={control}
              name="filtering"
              render={({ field, fieldState }) => {
                const validFilters = field.value.map<ValidatedGridFilterItem>(
                  f => ({ ...f, isValid: true })
                );
                return (
                  <Stack spacing={0.5} useFlexGap>
                    {fieldState.error ? (
                      <FormHelperText error>
                        {fieldState.error.message}
                      </FormHelperText>
                    ) : null}
                    <FilterPanel
                      columns={columns}
                      validFilters={validFilters}
                      invalidFilters={[]}
                      handleFilterModelChange={field.onChange}
                      handleClearAllFilters={() => field.onChange([])}
                      hasActiveQueue
                      forceEnableClearButton={field.value.length > 0}
                      addFilterButtonProps={{
                        type: 'separate',
                        buttonProps: {
                          variant: 'outlined',
                          startIcon: null,
                          sx: { textWrap: 'nowrap' },
                        },
                      }}
                      clearFilterButtonProps={{
                        type: 'separate',
                        buttonProps: {
                          disabled: validFilters.length === 0,
                          variant: 'outlined',
                          sx: { ml: 'auto' },
                        },
                      }}
                    />
                  </Stack>
                );
              }}
            />
          </Stack>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 2, py: 2, alignItems: 'center', justifyContent: 'flex-end' }}
      >
        {state.type === 'edit' ? (
          <Button
            color="secondary"
            variant="outlined"
            disabled={!canEditView || isDeleting}
            onClick={() => setIsDeleteDialogOpen(true)}
            data-cy="saved-view-delete-action"
          >
            {intl.formatMessage({
              id: 'containers.savedViews.drawer.deleteButton',
            })}
          </Button>
        ) : null}
        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          {state.type === 'edit' ? (
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={onDuplicate}
              data-cy="saved-view-duplicate-action"
            >
              {intl.formatMessage({
                id: 'containers.savedViews.drawer.duplicateButton',
              })}
            </Button>
          ) : (
            <Button variant="outlined" color="secondary" onClick={onClose}>
              {intl.formatMessage({
                id: 'containers.savedViews.drawer.cancelButton',
              })}
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={!canEditView || isSaving}
            endIcon={
              isSaving ? <CircularProgress size={16} color="inherit" /> : null
            }
            data-cy="saved-view-submit-action"
          >
            {state.type === 'create'
              ? intl.formatMessage({ id: 'containers.savedViews.createButton' })
              : state.type === 'duplicate'
                ? intl.formatMessage({
                    id: 'containers.savedViews.drawer.duplicateButton',
                  })
                : intl.formatMessage({
                    id: 'containers.savedViews.drawer.saveButton',
                  })}
          </Button>
        </Stack>
      </Stack>
      {state.type === 'edit' ? (
        <DeleteConfirmationDialog
          open={isDeleteDialogOpen}
          title={intl.formatMessage({
            id: 'containers.savedViews.drawer.deleteDialog.title',
          })}
          description={intl.formatMessage(
            { id: 'containers.savedViews.drawer.deleteDialog.description' },
            { viewName: state.savedView.name }
          )}
          isLoading={isDeleting}
          onCancel={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      ) : null}
    </Stack>
  );
};
